import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { RouteInfo, ROUTES, getItemId } from '../../classes/routes';
import { userMayAccessPath } from '../../utils/menu-path-access';
import { buildAnchorReportsMap, flattenNavigableItems, FlattenedNavItem } from './sidebar-menu.util';
import { UsersService } from '../../services/users.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { ReportsService } from '../../services/reports.service';
import { GlobalsService } from '../../services/globals.service';
import { PinnedMenuService } from '../../services/pinned-menu.service';
import { CommandPaletteService } from '../../services/command-palette.service';
import { RuntimeConfigLoaderModule } from 'runtime-config-loader';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, combineLatest, of } from 'rxjs';
import { switchMap, takeUntil, map, filter, catchError, distinctUntilChanged, debounceTime, startWith } from 'rxjs/operators';

declare const $: any;

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	menuItems: any[];
	searchTerm: any;
	userData: any;
	searchResults: any;
	user: any;
	messagecount: any;
	reports: any = [];
	access: any = ['/customers'];
	always_allowed = ['/logout', '/auth/permission'];
	config: any = false;
	datasets: any = false;
	favorites: any = [];
	updatingPrintConnect: any = false;
	activePrintConnect: any = false;
	pinnedItemsForDisplay: FlattenedNavItem[] = [];

	// Pre-computed cached properties — updated only when inputs change
	navState: boolean = false;
	isMobileMenuActive: boolean = false;
	isFavoriteRoute: boolean = false;
	pinnedSet: Set<string> = new Set();
	anchorReportsMap: Map<string, any[]> = new Map();
	reportTitleSet: Set<string> = new Set();

	private memoizedPathAllowed: (path: string) => boolean;
	private memoizationCache: Map<string, boolean> = new Map();
	private activeAnimations: any[] = [];
	private pinnedSub: { unsubscribe: () => void } | null = null;
	private flattenedCache: FlattenedNavItem[] | null = null;
	private currentUrl: string = '';

	constructor(
		public reportsService: ReportsService,
		private globalsService: GlobalsService,
		private userService: UsersService,
		private globalSearchService: GlobalSearchService,
		public router: Router,
		private pinnedMenuService: PinnedMenuService,
		private commandPaletteService: CommandPaletteService,
		private cdr: ChangeDetectorRef
	) {
		this.globalSearchService.configsubscription
			.pipe(takeUntil(this.destroy$))
			.subscribe(r => {
				this.config = r;
				this.cdr.markForCheck();
			});

		this.memoizedPathAllowed = this.buildMemoizedPathAllowed();
	}

	ngOnInit() {
		this.navState = this.userService.getNavState();
		this.isMobileMenuActive = $(window).width() <= 991;

		combineLatest([
			this.globalSearchService.user.pipe(
				filter(user => user?.user?.userid),
				distinctUntilChanged((prev, curr) => prev?.user?.userid === curr?.user?.userid),
				switchMap((user: any) => {
					this.user = user;
					return this.userService.getPrintConnectStatus(user.user.userid).pipe(
						map((res: any) => {
							this.activePrintConnect = res.status != 0;
							return user;
						}),
						catchError(() => {
							this.activePrintConnect = false;
							return of(user);
						})
					);
				})
			),
			this.reportsService.getReports().pipe(
				startWith([]),
				catchError(() => of([]))
			),
			this.globalsService.getFavorites().pipe(
				startWith([]),
				catchError(() => of([]))
			)
		]).pipe(
			takeUntil(this.destroy$),
			debounceTime(100)
		).subscribe(([user, reports, favorites]) => {
			this.user = user;
			this.reports = reports;
			this.favorites = favorites;
			this.memoizationCache.clear();
			this.memoizedPathAllowed = this.buildMemoizedPathAllowed();
			this.initializeMenuItems();
			this.flattenedCache = null;
			this.updateAnchorReportsMap();
			this.updateReportTitleSet();
			this.refreshPinnedForDisplay();
			this.updateIsFavoriteRoute();
			this.cdr.markForCheck();
		});

		this.pinnedSub = this.pinnedMenuService.subscribe((ids) => {
			this.pinnedSet = new Set(ids);
			this.refreshPinnedForDisplay();
			this.cdr.markForCheck();
		});

		this.globalSearchService.user
			.pipe(
				takeUntil(this.destroy$),
				switchMap((user: any) =>
					user?.user?.department === '1'
						? this.globalSearchService.datasets.pipe(
							map(datasets => ({ user, datasets })),
							catchError(() => of({ user, datasets: null }))
						)
						: of({ user, datasets: null })
				)
			)
			.subscribe(({ datasets }) => {
				if (datasets) {
					this.datasets = datasets;
					this.cdr.markForCheck();
				}
			});

		this.globalSearchService.messages
			.pipe(takeUntil(this.destroy$))
			.subscribe((results: any) => {
				this.messagecount = results;
				this.cdr.markForCheck();
			});

		this.globalSearchService.searchTerm
			.pipe(takeUntil(this.destroy$))
			.subscribe((newValue: string) => {
				this.searchTerm = newValue;
				this.cdr.markForCheck();
			});

		this.globalSearchService.userData
			.pipe(takeUntil(this.destroy$))
			.subscribe((newValue: string) => {
				this.userData = newValue;
				this.cdr.markForCheck();
			});

		this.globalSearchService.searchResults
			.pipe(takeUntil(this.destroy$))
			.subscribe((newValue: string) => {
				this.searchResults = newValue;
				this.cdr.markForCheck();
			});

		// Update isFavoriteRoute on every navigation
		this.router.events.pipe(
			takeUntil(this.destroy$),
			filter(e => e instanceof NavigationEnd)
		).subscribe((e: NavigationEnd) => {
			this.currentUrl = e.urlAfterRedirects.split('?')[0];
			this.updateIsFavoriteRoute();
			this.cdr.markForCheck();
		});

		this.currentUrl = this.router.url.split('?')[0];
		this.updateIsFavoriteRoute();
	}

	private buildMemoizedPathAllowed(): (path: string) => boolean {
		const cache = this.memoizationCache;
		const self = this;
		return function(path: string): boolean {
			if (cache.has(path)) return cache.get(path)!;
			const result = self.pathAllowed(path);
			cache.set(path, result);
			return result;
		};
	}

	private initializeMenuItems(): void {
		this.menuItems = ROUTES.filter(menuItem => {
			if (this.always_allowed.includes(menuItem.path)) return true;
			return this.memoizedPathAllowed(menuItem.path);
		});
	}

	private updateAnchorReportsMap(): void {
		this.anchorReportsMap = buildAnchorReportsMap(this.reports, this.config?.env?.package);
	}

	private updateReportTitleSet(): void {
		const set = new Set<string>();
		if (Array.isArray(this.reports)) {
			for (const report of this.reports) {
				if (report && typeof report === 'object' && 'title' in report) {
					set.add(report.title);
				}
			}
		}
		this.reportTitleSet = set;
	}

	private updateIsFavoriteRoute(): void {
		this.isFavoriteRoute = (this.favorites || []).some((f: any) => f.path === this.currentUrl);
	}

	getAnchorReports(anchor: any): any[] {
		return this.anchorReportsMap.get(anchor) || [];
	}

	isTitleInReports(title: string): boolean {
		return this.reportTitleSet.has(title);
	}

	showMenuItem(menuItem: RouteInfo): boolean {
		if (menuItem.storefrontStandaloneOnly) {
			return this.config?.env?.storefrontStandalone === true &&
				(!menuItem.package || menuItem.package === 'all' || menuItem.package === this.config?.env?.package);
		}
		if (!menuItem.package) return true;
		return menuItem.package === 'all' || menuItem.package === this.config?.env?.package;
	}

	showChild(item: any): boolean {
		if (item.hideWhenStorefrontStandalone && this.config?.env?.storefrontStandalone) return false;
		if (!item.package) return true;
		return item.package === 'all' || item.package === this.config?.env?.package;
	}

	setDataSet(event: any) {
		localStorage.removeItem('app');
		this.globalSearchService.setPackage('app', event.config.env.package);
		this.globalSearchService.configsubscription.next(event.config);
		this.router.navigate(['/']);
		this.config = event.config;
		RuntimeConfigLoaderModule.forRoot({ configUrl: './assets/config/config.' + event.config + '.json' });
	}

	refreshPinnedForDisplay(): void {
		const ids = this.pinnedMenuService.pinnedItems;
		this.pinnedSet = new Set(ids);
		const flat = this.getFlattenedMenuItems();
		const idSet = new Set(flat.map(f => f.id));
		this.pinnedItemsForDisplay = ids
			.filter(id => id !== 'logout' && idSet.has(id))
			.map(id => flat.find(f => f.id === id)!)
			.filter(Boolean);
	}

	togglePin(itemId: string, event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this.pinnedMenuService.togglePin(itemId);
	}

	isPinned(itemId: string): boolean {
		return this.pinnedSet.has(itemId);
	}

	getItemId(path: string): string {
		return getItemId(path || '');
	}

	isPinnablePath(path: string): boolean {
		return getItemId(path || '') !== 'logout';
	}

	pathAllowed(path: string): boolean {
		return userMayAccessPath(path, this.user);
	}

	navTo(link: string) {
		if (link !== '') {
			this.router.navigate([link, { skipLocationChange: true }]);
		}
	}

	openCommandPalette(): void {
		if (this.isMobileMenuActive) {
			const body = document.getElementsByTagName('body')[0];
			const toggle = document.getElementsByClassName('navbar-toggler')[0] as HTMLElement;
			const layer = document.getElementsByClassName('close-layer')[0];
			body.classList.remove('nav-open');
			if (toggle) toggle.classList.remove('toggled');
			if (layer) layer.remove();
		}
		this.commandPaletteService.open();
	}

	isMobileMenu() {
		return this.isMobileMenuActive;
	}

	collapse(event: any) {
		const children = document.querySelector('.children');
		if (children) children.classList.remove('show');
	}

	resetCount() {
		this.messagecount = 0;
		this.cdr.markForCheck();
	}

	onInput(event: any) {
		this.globalSearchService.searchTerm.next(event.target.value);
	}

	selectParentMarker(element: string, secondref: string) {
		const el = $(element);
		$('.children').removeClass('show');
		$('.nav .nav-item').removeClass('active');

		if (el.length) {
			const sidebar = $('.sidebar-wrapper');
			const sidebarOffset = sidebar.offset()?.top ?? 0;
			const elementOffset = el.offset()?.top ?? 0;
			const targetScroll = sidebar.scrollTop() + elementOffset - sidebarOffset;
			const animation = sidebar.animate({ scrollTop: targetScroll }, 100);
			this.activeAnimations.push(animation);
			this.addAnimationCleanup(animation);
			$(`${secondref} .children`).addClass('show');
			el.addClass('active');
		} else {
			$(`${secondref} .children`).addClass('show');
		}
	}

	selectMarker(element: string, secondref: string) {
		const el = $(element);
		if (el) {
			const sidebar = $('.sidebar-wrapper');
			if (el.offset()) {
				let count = el.offset().top - sidebar.offset().top - sidebar.scrollTop();
				if (count < 0) {
					count = el.offset().top - sidebar.offset().top + sidebar.scrollTop();
				}
				$('.children').removeClass('show');
				$(secondref).addClass('show');
				const animation = sidebar.animate({ scrollTop: count }, 100);
				this.activeAnimations.push(animation);
				this.addAnimationCleanup(animation);
			} else {
				$('.children').removeClass('show');
				$(secondref).addClass('show');
			}
		}
	}

	getFlattenedMenuItems(): FlattenedNavItem[] {
		if (this.flattenedCache) return this.flattenedCache;

		const pkg = this.config?.env?.package;
		const flat = flattenNavigableItems(this.menuItems || [], pkg || '', {
			storefrontStandalone: this.config?.env?.storefrontStandalone
		});
		const withAnchorReports: FlattenedNavItem[] = [...flat];
		for (const menuItem of this.menuItems || []) {
			if (!menuItem.anchor) continue;
			for (const tr of this.getAnchorReports(menuItem.anchor)) {
				const path = (tr as any).nglink;
				if (path) {
					withAnchorReports.push({
						id: getItemId(path),
						path,
						title: (tr as any).title || '',
						icon: 'article'
					});
				}
			}
		}
		this.flattenedCache = withAnchorReports;
		return withAnchorReports;
	}

	addFavorites() {
		const path = this.currentUrl || this.router.url.split('?')[0];
		const entry = this.getFlattenedMenuItems().find(item => item.path === path);
		this.globalsService.addFavorite({ path, icon: entry?.icon ?? '', title: entry?.title ?? '' }).subscribe((faves: any) => {
			this.favorites = faves;
			this.updateIsFavoriteRoute();
			this.cdr.markForCheck();
		});
	}

	removeFavorites() {
		const path = this.currentUrl || this.router.url.split('?')[0];
		const fave = (this.favorites || []).find((f: any) => f.path === path);
		if (!fave?.faveid) return;
		this.globalsService.removeFavorite({ faveid: fave.faveid }).subscribe((faves: any) => {
			this.favorites = faves;
			this.updateIsFavoriteRoute();
			this.cdr.markForCheck();
		});
	}

	isFavorite(): boolean {
		return this.isFavoriteRoute;
	}

	trackByAnchor(index: number, item: any): string {
		return item.anchor || item.path || String(index);
	}

	trackByPath(index: number, item: any): string {
		return item.path || item.nglink || String(index);
	}

	trackByFaveId(index: number, item: any): string {
		return item.faveid || item.path || String(index);
	}

	trackByPinnedId(index: number, item: FlattenedNavItem): string {
		return item.id;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.pinnedSub?.unsubscribe();
		this.pinnedSub = null;
		this.memoizationCache.clear();
		this.flattenedCache = null;
		this.activeAnimations.forEach(animation => {
			if (animation && typeof animation.stop === 'function') animation.stop();
		});
		this.activeAnimations = [];
	}

	togglePrintConnect(event: any) {
		this.activePrintConnect = !this.activePrintConnect;
		this.updatingPrintConnect = true;
		this.userService.saveprintconnectstatus(this.user.user.userid, this.activePrintConnect ? 1 : 0).subscribe(() => {
			this.updatingPrintConnect = false;
			this.cdr.markForCheck();
		});
	}

	private addAnimationCleanup(animation: any): void {
		if (animation && typeof animation.promise === 'function') {
			animation.promise().then(() => {
				const index = this.activeAnimations.indexOf(animation);
				if (index > -1) this.activeAnimations.splice(index, 1);
			});
		}
	}
}
